import { RecoverableBinding, recoverableFieldWithOptions } from "../../../ServiceDesk/recovery/recoverable-control";
import { UiControlConstants } from "../../common/uicontrol-constants";

export class SelectBinding extends RecoverableBinding {
  /** true/false. Whether the control is disabled or enabled. Defaults to false. */
  public isDisabled = "<?";

  /** The field of the options object to be displayed if it is not an array of strings.*/
  public displayProperty = "@?";

  /** true/false. Whether there is an outline around the control. Defaults to false.*/
  public hasBorder = "<?";

  /** fn called when selection has changed. Parameter 'item' is passed along which contains the selected item.*/
  public onChange = "&?";

  /** Array of sd-options to be displayed.*/
  public options = "<?";

  /** The text displayed when nothing has been chosen. Defaults to 'Select an option'.*/
  public placeholderText = "@?";

  /** The value of the selected field from the 'options' binding.*/
  public selectedItem = "=?";

  /** The identifier for all business intelligence (BI) actions triggered by this component.*/
  public biId = "@?";

  /** fn called to check for equality of selected item with rest of options. Signature: (args: {option: any, selectedItem: any}) => boolean*/
  public itemSelectionPredicate = "&?";

  /** Given an item from the options will return the unique id of that option. Required for recovery of this control. */
  public itemIdGetter = "&?";

  /** If true, the right side of the menu will be aligned with the right side of the control. Otherwise, it aligns left. */
  public alignMenuRight = "<?";

  /** Added for accessibility */
  public ariaLabelledby = "@?";

  /** Added for accessibility. Defaults to false.*/
  public ariaRequired = "<?";
}

/** Controller for select component. */
export class SelectController {
  public displayProperty: string;
  public hasSelectedItem = false;
  public isDisabled: boolean;
  public isExpanded = false;
  @recoverableFieldWithOptions({ preventAutomaticRecovery: true })
  public selectedItem: any;
  public onChange: (args: { item: any } | any) => {};
  public optionProperty: string;
  public options: Array<any>;
  public required: boolean;
  public selectedItemText: string;
  public selectOptionText = "Select an option";
  public transclude: boolean;
  public placeholderText: string;
  public focusedItemIndex: number;
  public selectedItemIndex: number;
  public itemSelectionPredicate: (args: { option: any; selectedItem: any }) => boolean;
  public itemIdGetter: (arg: { item: any }) => string;
  public recoveryId: string;
  public alignMenuRight: boolean;
  // @ngInject
  constructor(private $scope: ng.IScope, private $timeout: ng.ITimeoutService, private $element: ng.IAugmentedJQuery) {}

  public $onInit() {
    this.selectOptionText =
      !!this.placeholderText && this.placeholderText !== "" ? this.placeholderText : this.selectOptionText;
    this.refreshView();
    this.$scope.$watch(
      () => {
        return this.selectedItem;
      },
      (newVal: any) => {
        if (!newVal) {
          this.updateSelectedItemText(this.selectOptionText);
          this.hasSelectedItem = false;
        }
      }
    );
    if (this.recoveryId && !this.itemIdGetter) {
      throw new Error("recovery id specified, but no itemIdGetter was specified");
    }
  }

  public $onChanges(changesObj: any) {
    this.refreshView();
  }

  $sdRecoveryOnComplete(data: any) {
    this.selectedItem = this.findMatchingOptionFromRecoveryData(data);
    if (angular.isFunction(this.onChange)) {
      this.onChange({ item: this.selectedItem });
    }
  }

  private findMatchingOptionFromRecoveryData(data: any) {
    let recoveredId = this.itemIdGetter({ item: data.selectedItem });
    return this.options.find((o) => this.itemIdGetter({ item: o }) == recoveredId);
  }

  $sdRecoveryIsModelValid(data: any) {
    return data.selectedItem && this.findMatchingOptionFromRecoveryData(data);
  }

  /** Toggles the select dropdown. */
  public toggleExpandSelect($event: ng.IAngularEvent) {
    if (this.isDisabled !== true) {
      this.isExpanded = !this.isExpanded;
      this.focusedItemIndex = this.isExpanded ? this.selectedItemIndex || 0 : undefined;
    }
  }

  public setExpanderFocus() {
    this.$timeout(() => {
      this.$element.find(".select-anchor").get(0).focus();
    }, 0);
  }

  /** Collapses the dropdown. */
  public collapseSelect() {
    this.isExpanded = false;
    this.focusedItemIndex = undefined;
  }

  /** Gets executed when the select option is changed. */
  public onSelectChange(selectedItem: any, optionIndex: number) {
    let isModelChanged = this.selectedItem !== selectedItem;
    this.updateSelectedItem(selectedItem, optionIndex);

    if (isModelChanged && angular.isFunction(this.onChange)) {
      this.onChange({ item: selectedItem });
    }
  }

  /** Updates the selected item and display text. */
  public updateSelectedItem(selectedItem: any, optionIndex: number) {
    this.hasSelectedItem = true;
    this.selectedItem = selectedItem;
    this.selectedItemIndex = optionIndex;
    this.updateSelectedItemText(this.getSelectedItemText());
  }

  /** Updates the selected item display text. */
  public updateSelectedItemText(text: string): void {
    if (this.selectedItemText !== text) {
      this.selectedItemText = text;
    }
  }

  private refreshView(): void {
    let isOptionsArray = angular.isArray(this.options);
    this.transclude = !isOptionsArray;
    this.optionProperty = angular.isString(this.displayProperty) ? this.displayProperty : undefined;
  }

  private getSelectedItemText(): string {
    return !!this.optionProperty ? this.selectedItem[this.optionProperty] : this.selectedItem;
  }
}

/** Controller for select component. */
export class SelectOptionController {
  public value: any;
  public label: string;
  public selectController: SelectController;
  public isSelected = false;
  public optionIndex: number;
  public optionLength: number;

  // @ngInject
  constructor(private $scope: ng.IScope, private uiControlConstants: UiControlConstants) {}

  public $onInit() {
    this.refreshView();
    this.$scope.$watch(
      () => {
        return this.selectController.selectedItem;
      },
      (newVal: any) => {
        this.refreshView();
      }
    );
    if (this.selectController.options.length) {
      this.optionLength = this.selectController.options.length;
    }
  }

  public $onChanges(changesObj: any) {
    this.refreshView();
  }

  public updateFocusedOption() {
    return this.selectController.focusedItemIndex == this.optionIndex;
  }

  public navigateDropDown($event: any) {
    switch ($event.keyCode) {
      case this.uiControlConstants.keyCode.tabKeyCode:
      case this.uiControlConstants.keyCode.spaceKeyCode:
      case this.uiControlConstants.keyCode.enterKeyCode:
        this.selectController.onSelectChange(this.value, this.optionIndex);
        this.selectController.collapseSelect();
        this.selectController.setExpanderFocus();
        break;
      case this.uiControlConstants.keyCode.escapeKeyCode:
        this.selectController.collapseSelect();
        break;
      case this.uiControlConstants.keyCode.upArrowKeyCode:
        this.navigateToPreviousOption($event.target);
        break;
      case this.uiControlConstants.keyCode.downArrowKeyCode:
        this.navigateToNextOption($event.target);
        break;
    }

    if (
      $event.keyCode == this.uiControlConstants.keyCode.upArrowKeyCode ||
      $event.keyCode == this.uiControlConstants.keyCode.downArrowKeyCode ||
      $event.keyCode == this.uiControlConstants.keyCode.tabKeyCode ||
      $event.keyCode == this.uiControlConstants.keyCode.spaceKeyCode ||
      $event.keyCode == this.uiControlConstants.keyCode.enterKeyCode ||
      $event.keyCode == this.uiControlConstants.keyCode.escapeKeyCode
    ) {
      $event.preventDefault();
      $event.stopPropagation();
    }
  }

  public navigateToNextOption(target: any) {
    if (this.selectController.focusedItemIndex < this.selectController.options.length - 1) {
      this.selectController.focusedItemIndex = this.selectController.focusedItemIndex + 1;
    } else {
      this.selectController.focusedItemIndex = 0;
    }
  }

  public navigateToPreviousOption(target: any) {
    if (this.selectController.focusedItemIndex) {
      this.selectController.focusedItemIndex = this.selectController.focusedItemIndex - 1;
    } else {
      this.selectController.focusedItemIndex = this.selectController.options.length - 1;
    }
  }

  /** Select the option on-click */
  public onClick($event: any) {
    this.selectController.onSelectChange(this.value, this.optionIndex);
  }

  private refreshView() {
    if (angular.isFunction(this.selectController.itemSelectionPredicate)) {
      this.isSelected = this.selectController.itemSelectionPredicate({
        option: this.value,
        selectedItem: this.selectController.selectedItem
      });
    } else {
      this.isSelected = this.value === this.selectController.selectedItem;
    }

    this.label = this.label ? this.label : this.value;
    if (this.isSelected) {
      this.selectController.updateSelectedItem(this.value, this.optionIndex);
    } else if (!this.selectController.hasSelectedItem) {
      // Update the placeholder text.
      this.selectController.updateSelectedItemText(this.selectController.selectOptionText);
    }
  }
}
export const sdSelect: ng.IComponentOptions = {
  bindings: <any>new SelectBinding(),
  transclude: { options: "?sdOptions" },
  controller: SelectController,
  templateUrl: "../ShellCommon/Controls/select/select.html"
};
export const sdOption: ng.IComponentOptions = {
  bindings: {
    value: "<",
    label: "@?",
    optionIndex: "<",
    biId: "@?"
  },
  controller: SelectOptionController,
  require: {
    selectController: "^sdSelect"
  },
  template: `<li class="c-menu-item" ng-click="$ctrl.onClick($event)">
                <a  aria-checked="{{$ctrl.isSelected}}"
                    role="option"
                    aria-posinset="{{$ctrl.optionIndex+1}}"
                    aria-setsize="{{$ctrl.optionLength}}"
                    href=""
                    aria-selected="{{$ctrl.isSelected}}"
                    ng-bind="::$ctrl.label"
                    focus-me="$ctrl.updateFocusedOption()"
                    ng-keydown="$ctrl.navigateDropDown($event)"
                    sd-page-action-trigger="{ events:'click', keypresses:'tab' }"
                    data-bi-id="{{::$ctrl.biId}}"
                    sd-fill-bi-slot
                    tabindex="-1">
                </a>
             </li>`
};
